const ELEMENT_NODE = 1
const COMMENT_NODE = 8

export const DEFAULT_RICH_TEXT_ALLOWED_TAGS = new Set([
	'a',
	'b',
	'br',
	'em',
	'h2',
	'i',
	'li',
	'ol',
	'p',
	's',
	'strong',
	'u',
	'ul'
])

export const JOB_DESCRIPTION_ALLOWED_TAGS = new Set([
	'br',
	'em',
	'h2',
	'li',
	'ol',
	'p',
	's',
	'strong',
	'u',
	'ul'
])

const DROP_WITH_CONTENT_TAGS = new Set([
	'base',
	'embed',
	'form',
	'iframe',
	'input',
	'link',
	'math',
	'meta',
	'object',
	'script',
	'select',
	'style',
	'svg',
	'template',
	'textarea'
])

const ALLOWED_ATTRS = {
	a: ['href', 'target', 'rel']
}

export const escapeHtml = value =>
	String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;')

const safeHref = value => {
	if (!value || typeof value !== 'string') {
		return ''
	}

	const href = value.trim()
	const normalized = href.toLowerCase()

	if (
		normalized.startsWith('http://') ||
		normalized.startsWith('https://') ||
		normalized.startsWith('mailto:') ||
		normalized.startsWith('tel:') ||
		normalized.startsWith('/') ||
		normalized.startsWith('#') ||
		normalized.startsWith('?')
	) {
		return href
	}

	return ''
}

const unwrapNode = node => {
	while (node.firstChild) {
		node.parentNode.insertBefore(node.firstChild, node)
	}

	node.remove()
}

const sanitizeElementAttributes = node => {
	const tag = node.tagName.toLowerCase()
	const allowed = ALLOWED_ATTRS[tag] || []

	Array.from(node.attributes).forEach(attribute => {
		const attrName = attribute.name.toLowerCase()

		if (attrName.startsWith('on') || !allowed.includes(attrName)) {
			node.removeAttribute(attribute.name)
		}
	})

	if (tag !== 'a') {
		return
	}

	const href = node.getAttribute('href')
	const sanitizedHref = safeHref(href)

	if (!sanitizedHref) {
		node.removeAttribute('href')
		node.removeAttribute('target')
		node.removeAttribute('rel')
		return
	}

	node.setAttribute('href', sanitizedHref)
	node.setAttribute('target', '_blank')
	node.setAttribute('rel', 'noopener noreferrer')
}

const sanitizeNode = (root, allowedTags) => {
	let i = 0

	while (i < root.childNodes.length) {
		const node = root.childNodes[i]

		if (node.nodeType === COMMENT_NODE) {
			node.remove()
			continue
		}

		if (node.nodeType !== ELEMENT_NODE) {
			i++
			continue
		}

		const tag = node.tagName.toLowerCase()

		if (DROP_WITH_CONTENT_TAGS.has(tag)) {
			node.remove()
			continue
		}

		if (!allowedTags.has(tag)) {
			unwrapNode(node)
			continue
		}

		sanitizeElementAttributes(node)
		sanitizeNode(node, allowedTags)
		i++
	}
}

export const sanitizeRichTextHtml = (input, options = {}) => {
	if (!input) {
		return ''
	}

	const raw = String(input)
	const hasMarkup = /<\/?[a-z][\s\S]*>/i.test(raw)

	if (!hasMarkup) {
		return escapeHtml(raw).replace(/\n/g, '<br>')
	}

	if (typeof document === 'undefined' || !document.createElement) {
		return escapeHtml(raw)
	}

	const allowedTags = options.allowedTags || DEFAULT_RICH_TEXT_ALLOWED_TAGS
	const template = document.createElement('template')

	template.innerHTML = raw
	sanitizeNode(template.content, allowedTags)

	return template.innerHTML
}
